import { BlitzPage } from "@blitzjs/next"
import { useMutation } from "@blitzjs/rpc"
import { useRouter } from "next/router"
import dynamic from "next/dynamic"
import Link from "next/link"
import Layout from "app/core/layouts/Layout"
import Footer from "components/footer"
import { STATIC_ASSETS_CLOUDFRONT_URL } from "app/constants"
import { useAtom } from "jotai"
import { trackingPreferencesAtom, CookiePreferences } from "app/state"
import { Formik, Form, Field, ErrorMessage } from "formik"
import { z } from "zod"
import { validateZodSchema } from "blitz"
import { useState, useEffect } from "react"
import sendContactMessage from "app/contact/mutations/sendContactMessage"

const Header = dynamic(() => import("components/header"), { ssr: false })

interface TrackingToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
  label: string
  description: string
}

const TrackingToggle = ({
  checked,
  onChange,
  disabled,
  label,
  description,
}: TrackingToggleProps) => (
  <div className="flex items-center justify-between border-b border-gray-200 py-4">
    <div className="pr-4">
      <p className="font-semibold text-brand-green-dark">{label}</p>
      <p className="text-sm text-gray-600">{description}</p>
    </div>
    {disabled ? (
      <span className="whitespace-nowrap text-sm font-medium text-gray-500">Always Active</span>
    ) : (
      <button
        onClick={() => onChange(!checked)}
        className={`relative h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
          checked ? "bg-brand-green-dark" : "bg-gray-300"
        }`}
        aria-label={`Toggle ${label}`}
      >
        <span
          className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
            checked ? "translate-x-5" : "translate-x-0"
          }`}
        />
      </button>
    )}
  </div>
)

const COOKIE_DATA = [
  {
    cookie: "_ga",
    provider: "Google",
    purpose: "Distinguishes unique users by assigning a randomly generated number",
    expiration: "2 years",
    type: "Third-party",
  },
  {
    cookie: "_gid",
    provider: "Google",
    purpose: "Distinguishes users for analytics",
    expiration: "24 hours",
    type: "Third-party",
  },
  {
    cookie: "_gat",
    provider: "Google",
    purpose: "Throttles request rate to limit data collection",
    expiration: "1 minute",
    type: "Third-party",
  },
]

const CPRA_OPTIONS = [
  { id: "globalOptOut", label: "Global opt-out from selling/sharing personal info" },
  {
    id: "noCrossBehavioral",
    label: "Do not sell my personal info for cross-context behavioral advertising",
  },
  { id: "limitSensitive", label: "Limit the use of my sensitive personal info" },
  { id: "accessRequest", label: "Request for access to my personal information" },
  { id: "rightForgotten", label: "Right to be Forgotten" },
  { id: "dataPortability", label: "Right to Data Portability" },
]

const cpraSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  globalOptOut: z.boolean().optional(),
  noCrossBehavioral: z.boolean().optional(),
  limitSensitive: z.boolean().optional(),
  accessRequest: z.boolean().optional(),
  rightForgotten: z.boolean().optional(),
  dataPortability: z.boolean().optional(),
  formVersion: z.literal("v2"),
  website: z.string().optional(),
})

const ManageTracking: BlitzPage = () => {
  const [preferences, setPreferences] = useAtom(trackingPreferencesAtom)
  const [draft, setDraft] = useState<CookiePreferences>({ analytics: true, sessionReplay: true })
  const [mounted, setMounted] = useState(false)
  const [formSent, setFormSent] = useState<"init" | "sending" | "sent">("init")
  const [sendContactMessageMutation] = useMutation(sendContactMessage)
  const router = useRouter()

  useEffect(() => {
    setMounted(true)
  }, [])

  // Sync draft from persisted preferences on mount in case we've already set preferences
  useEffect(() => {
    if (preferences !== undefined) {
      setDraft(preferences)
    }
  }, [mounted])

  // Toggles update draft state only — nothing persists until "Save Preferences" is clicked.
  const handlePreferenceChange = (key: keyof CookiePreferences, value: boolean) => {
    setDraft((prev) => ({ ...prev, [key]: value }))
  }

  const handleSave = () => {
    setPreferences(draft)
    router.back()
  }

  const handleCpraSubmit = async (values: z.infer<typeof cpraSchema>) => {
    setFormSent("sending")

    const selectedOptions = CPRA_OPTIONS.filter((opt) => values[opt.id as keyof typeof values]).map(
      (opt) => opt.label
    )

    // Re-uses the contact form mutation. Sends to our shared treeequityscore inbox.
    await sendContactMessageMutation({
      name: values.name,
      email: values.email,
      questionType: "CPRA Data Request",
      comment: `CPRA Data Request from ${values.name} (${values.email}):\n\nSelected options:\n${
        selectedOptions.map((opt) => `- ${opt}`).join("\n") || "None selected"
      }`,
      formVersion: values.formVersion,
      website: values.website,
    })

    setFormSent("sent")
  }

  return (
    <div className="min-h-screen">
      <section
        style={{
          backgroundImage: `url(${STATIC_ASSETS_CLOUDFRONT_URL}/Parcel_Tree_Banner_DkGr.jpg)`,
          backgroundSize: "cover",
          backgroundPosition: "center center",
          backgroundRepeat: "no-repeat",
          imageRendering: "crisp-edges",
        }}
      >
        <div className="bg-white">
          <Header />
        </div>
        <div className="m-auto w-4/5 pb-5 pt-5">
          <div>
            <p className="text-3xl font-medium text-white md:leading-snug lg:text-4xl xl:text-4xl">
              Cookie & Tracking Policy
            </p>
          </div>
        </div>
      </section>
      <section className="bg-gradient-to-b from-white to-[#ECF5F2]">
        <div className="m-auto w-11/12 lg:w-4/5 xl:w-1/2">
          <div className="h-10"></div>

          <p className="pt-8 text-2xl text-brand-green-dark lg:text-4xl">
            Manage Tracking Preferences
          </p>
          <p className="mb-4 pt-5 text-sm lg:text-base">
            You can manage your tracking & cookie preferences below. Essential cookies cannot be
            disabled as they are necessary for the website to function properly.
          </p>

          {/** This should only ever be rendered in the browser */}
          {mounted && (
            <div className="my-6 rounded-lg bg-white p-6 shadow-md">
              {(() => {
                const allDisabled = !draft.analytics && !draft.sessionReplay
                return (
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4">
                    <div className="pr-4">
                      <p className="font-semibold text-brand-green-dark">All Tracking</p>
                      <p className="text-sm text-gray-600">
                        {allDisabled
                          ? "Enable all non-essential tracking."
                          : "Disable all non-essential tracking."}
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        setDraft({
                          analytics: allDisabled ? true : false,
                          sessionReplay: allDisabled ? true : false,
                        })
                      }
                      className={`flex-shrink-0 rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${
                        allDisabled
                          ? "border-brand-green-dark text-brand-green-dark hover:border-brand-green-darker hover:text-brand-green-darker"
                          : "border-red-600 text-red-600 hover:border-red-800 hover:text-red-800"
                      }`}
                    >
                      {allDisabled ? "Enable All" : "Disable All"}
                    </button>
                  </div>
                )
              })()}
              <TrackingToggle
                label="Analytics Cookies"
                description="Help us understand how visitors interact with our website (Google Analytics)."
                checked={draft.analytics}
                onChange={(checked) => handlePreferenceChange("analytics", checked)}
              />
              <TrackingToggle
                label="Session Replay"
                description="Anonymized recordings of your session used to analyze site behavior and identify bugs in our site. (Sentry)"
                checked={draft.sessionReplay}
                onChange={(checked) => handlePreferenceChange("sessionReplay", checked)}
              />
              <TrackingToggle
                label="Essential Cookies"
                description="Required for the website to function. These cannot be disabled."
                checked={true}
                onChange={() => {}}
                disabled={true}
              />
              <div className="flex justify-end pt-4">
                <button
                  onClick={handleSave}
                  className="rounded-full bg-brand-green-dark px-4 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-green-darker"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          )}
          {/* Introduction */}
          <p className="pt-5 text-2xl text-brand-green-dark lg:text-4xl">What are cookies?</p>
          <p className="pt-5 text-sm lg:text-base">
            Cookies are small text files that are placed on your computer or mobile device when you
            visit a website. They are widely used to make websites work more efficiently and to
            provide information to website owners. Cookies help us remember your preferences,
            understand how you use our website, and improve your overall experience.
          </p>

          <p className="pt-8 text-2xl text-brand-green-dark lg:text-4xl">How we use cookies</p>
          <p className="pt-5 text-sm lg:text-base">
            Tree Equity Score uses cookies to enhance your browsing experience, analyze site
            traffic, and understand where our visitors come from. We use both first-party cookies
            (set by us) and third-party cookies (set by our partners) to provide our services and
            measure their effectiveness.
          </p>

          {/* Cookie Details Table */}
          <p className="pt-8 text-2xl text-brand-green-dark lg:text-4xl">Cookie Details</p>
          <p className="mb-4 pt-5 text-sm lg:text-base">
            The following table lists the specific cookies used on our website:
          </p>

          <div className="my-6 overflow-x-auto">
            <table className="w-full rounded-lg bg-white shadow-md">
              <thead>
                <tr className="bg-brand-green-dark text-white">
                  <th className="px-4 py-3 text-left text-sm font-semibold">Cookie</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Provider</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Purpose</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Expiration</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold">Type</th>
                </tr>
              </thead>
              <tbody>
                {COOKIE_DATA.map((cookie, index) => (
                  <tr key={cookie.cookie} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-4 py-3 font-mono text-sm">{cookie.cookie}</td>
                    <td className="px-4 py-3 text-sm">{cookie.provider}</td>
                    <td className="px-4 py-3 text-sm">{cookie.purpose}</td>
                    <td className="px-4 py-3 text-sm">{cookie.expiration}</td>
                    <td className="px-4 py-3 text-sm">{cookie.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CPRA Data Request Form */}
          <p className="pt-8 text-2xl text-brand-green-dark lg:text-4xl">
            California Privacy Rights (CPRA) Data Request
          </p>
          <p className="mb-4 pt-5 text-sm lg:text-base">
            If you are a California resident, you have certain rights regarding your personal
            information under the California Privacy Rights Act (CPRA). Use the form below to submit
            a data request.
          </p>

          <div className="my-6 rounded-lg bg-white p-6 shadow-md">
            {formSent === "sent" && (
              <div className="py-4 font-semibold text-brand-green-dark">
                Thank you for your request. We will process it and respond within 45 days as
                required by law.
              </div>
            )}
            {formSent === "sending" && (
              <div className="py-4 text-gray-600">Submitting your request...</div>
            )}
            {formSent === "init" && (
              <Formik
                initialValues={{
                  name: "",
                  email: "",
                  globalOptOut: false,
                  noCrossBehavioral: false,
                  limitSensitive: false,
                  accessRequest: false,
                  rightForgotten: false,
                  dataPortability: false,
                  formVersion: "v2" as const, // Anti-spam: must be present and match
                  website: "", // Honeypot; must stay empty
                }}
                onSubmit={handleCpraSubmit}
                validate={async (val) => {
                  return await validateZodSchema(cpraSchema)(val)
                }}
                validateOnChange={false}
              >
                <Form>
                  <div
                    style={{
                      position: "absolute",
                      left: "-9999px",
                      height: 0,
                      overflow: "hidden",
                    }}
                    aria-hidden="true"
                  >
                    <label htmlFor="cpra-website">Website</label>
                    <Field
                      type="text"
                      id="cpra-website"
                      name="website"
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="name"
                      className="mb-1 block font-semibold text-brand-green-dark"
                    >
                      Name *
                    </label>
                    <Field
                      type="text"
                      id="name"
                      name="name"
                      className="w-full rounded border border-gray-300 px-4 py-2 focus:border-brand-green focus:ring-brand-green"
                    />
                    <ErrorMessage
                      name="name"
                      component="div"
                      className="mt-1 text-sm text-red-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label
                      htmlFor="email"
                      className="mb-1 block font-semibold text-brand-green-dark"
                    >
                      Email *
                    </label>
                    <Field
                      type="email"
                      id="email"
                      name="email"
                      className="w-full rounded border border-gray-300 px-4 py-2 focus:border-brand-green focus:ring-brand-green"
                    />
                    <ErrorMessage
                      name="email"
                      component="div"
                      className="mt-1 text-sm text-red-500"
                    />
                  </div>

                  <div className="mb-4">
                    <p className="mb-2 font-semibold text-brand-green-dark">
                      Select your request(s):
                    </p>
                    {CPRA_OPTIONS.map((option) => (
                      <div key={option.id} className="mb-2 flex items-start">
                        <Field
                          type="checkbox"
                          id={option.id}
                          name={option.id}
                          className="mr-3 mt-1 h-4 w-4 rounded border-gray-300 text-brand-green focus:ring-brand-green"
                        />
                        <label htmlFor={option.id} className="text-sm text-gray-700">
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      className="rounded-full bg-brand-green-dark px-6 py-2 font-semibold uppercase tracking-wider text-white transition-colors hover:bg-brand-green-darker"
                    >
                      Submit Request
                    </button>
                  </div>
                </Form>
              </Formik>
            )}
          </div>

          <p className="pt-8 text-sm lg:text-base">
            For more information about how we handle your personal data, please see our{" "}
            <Link href="/privacy" className="text-brand-green-dark hover:text-brand-green">
              Privacy Policy
            </Link>
            .
          </p>

          <p className="pt-16 text-xs italic">Last updated February 20, 2026.</p>
          <div className="h-20"></div>
        </div>
      </section>
      <Footer />
    </div>
  )
}

ManageTracking.getLayout = (page) => <Layout title={"Manage Tracking Preferences"}>{page}</Layout>
export default ManageTracking
